import React, { useEffect, useState } from "react";
import { auth, db } from "../../firebase/config";
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { consumeRegistrationSuccessNotice } from "./registrationSuccessNotice";

const passwordResetSettings = {
  url: "https://myfarmingpal.com/login",
  handleCodeInApp: false,
};

const Login = ({ setUser, embedded = false, switchToRegister }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const [registrationSuccessNotice] = useState(() =>
    consumeRegistrationSuccessNotice(location.state)
  );
  const safeReturnTo =
    typeof location.state?.returnTo === "string" &&
    location.state.returnTo.startsWith("/stand/") &&
    !location.state.returnTo.startsWith("//")
      ? location.state.returnTo
      : "";

  useEffect(() => {
    if (!location.state?.registrationSuccessNotice) return;

    const { registrationSuccessNotice: _registrationSuccessNotice, ...restState } =
      location.state;
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(restState).length > 0 ? restState : null,
    });
  }, [location.pathname, location.state, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setLoggingIn(true);

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      const user = userCredential.user;

      if (setUser) setUser(user);

      const userSnap = await getDoc(doc(db, "users", user.uid));
      const userData = userSnap.exists() ? userSnap.data() : null;

      if (userData?.accountStatus === "deactivated") {
        navigate("/reactivate");
      } else {
        navigate(safeReturnTo || "/home");
      }
    } catch (error) {
      console.error(error);
      alert("Could not log in. Please check your email and password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    const cleanEmail = resetEmail.trim() || email.trim();

    if (!cleanEmail) {
      setResetMessage("Enter your email and we will send password reset instructions.");
      return;
    }

    try {
      setResetSending(true);
      setResetMessage("");
      await sendPasswordResetEmail(auth, cleanEmail, passwordResetSettings);
    } catch (error) {
      if (error?.code === "auth/unauthorized-continue-uri") {
        try {
          await sendPasswordResetEmail(auth, cleanEmail);
        } catch (fallbackError) {
          console.error("Password reset failed:", fallbackError);
        }
      } else {
        console.error("Password reset failed:", error);
      }
    } finally {
      setResetSending(false);
      setResetMessage(
        "If an account exists for that email, a password reset link has been sent."
      );
    }
  };

  const content = (
    <div className="auth-login-snippet-guard" data-nosnippet>
      <div className="auth-hero">
        <div className="auth-badge">Welcome back</div>
        <h2>Log in to MyFarmingPal</h2>
        <p>
          Check local listings, messages, jobs, land access, plant listings,
          farm records, and community updates.
        </p>
      </div>

      {registrationSuccessNotice && (
        <p className="auth-success-message" role="status">
          {registrationSuccessNotice}
        </p>
      )}

      <form onSubmit={handleLogin} className="auth-form">
        <div className="form-stack">
          <div>
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              id="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="auth-inline-link"
              onClick={() => {
                setShowResetForm((current) => !current);
                setResetEmail(email);
                setResetMessage("");
              }}
            >
              Forgot password?
            </button>
          </div>
        </div>

        {showResetForm && (
          <div className="auth-reset-panel">
            <label htmlFor="reset-email" className="form-label">
              Reset password email
            </label>
            <div className="auth-reset-row">
              <input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <button
                type="button"
                className="btn-soft"
                onClick={handlePasswordReset}
                disabled={resetSending}
              >
                {resetSending ? "Sending..." : "Send reset"}
              </button>
            </div>
            {resetMessage && <p className="auth-safe-message">{resetMessage}</p>}
          </div>
        )}

        <button type="submit" className="btn-primary auth-submit" disabled={loggingIn}>
          {loggingIn ? "Logging in..." : "Login"}
        </button>

        <p className="auth-switch">
          Don’t have an account?{" "}
          {embedded ? (
            <button type="button" onClick={switchToRegister}>
              Register
            </button>
          ) : (
            <Link to="/register">Register</Link>
          )}
        </p>
        <p className="auth-help-link">
          Need help accessing your account? <Link to="/contact?issue=account_access">Contact support</Link>
        </p>
<div className="legal-footer-links">
  <Link to="/terms">Terms</Link>
  <Link to="/privacy">Privacy</Link>
  <Link to="/community-rules">Community Rules</Link>
  <Link to="/safety">Safety</Link>
  <Link to="/contact">Contact</Link>
</div>

      </form>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="auth-page">
      <div className="auth-card">{content}</div>
    </div>
  );
};

export default Login;
